import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  Tooltip,
  Paper,
  Drawer,
  Divider,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Today,
  ViewDay,
  ViewWeek,
  CalendarViewMonth,
} from "@mui/icons-material";
import * as df from "date-fns";

// ── Types ─────────────────────────────────────────────────────────────

interface ScheduleMission {
  _id: string;
  callsign: string;
  launchBase: string;
  onStation: string | null;
  offStation: string | null;
  takeoff: string | null;
  landing: string | null;
  operatingArea: string;
  int_prime: string;
  int_secondary: string;
  platform?: {
    name: string;
    designator: string;
    fullName: string;
    color?: string;
  };
  armed: boolean;
  locked: boolean;
  hold: boolean;
  canx: boolean;
  ato_day: string;
  ato_mission_number: string;
  occurrenceDate?: string | null;
}

interface Platform {
  _id: string;
  name: string;
  designator: string;
  fullName?: string;
  color?: string;
  int_disciplines?: string[];
}

// ── Constants ─────────────────────────────────────────────────────────

// Fallback only until /api/operatingAreas answers.
const OPERATING_AREAS = [
  "AFG", "IRAN", "IRAQ", "YEMEN", "AFRICOM", "ALLOTHER",
  "NORTHCOM", "AUS", "INDOPACOM", "EUCOM", "SOUTHCOM", "SYRIA",
];

const INT_TYPES = ["FMV", "GEOINT", "MTI", "SIGINT", "MASINT"];

const GROUP_OPTIONS = ["PLATFORM", "ASSET", "NONE"];

type ViewMode = "monthly" | "weekly" | "daily";

// ── Component ─────────────────────────────────────────────────────────

export default function Schedule() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Latest ATO day that has missions. With no month in the URL the calendar
  // opens there instead of on an empty current month; the URL sync below
  // waits for this answer so it cannot race and pin "today" first.
  const [latestDate, setLatestDate] = useState<string | null>(null);
  const [datesReady, setDatesReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/main/dates")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d?.latest) setLatestDate(d.latest); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setDatesReady(true); });
    return () => { cancelled = true; };
  }, []);

  // ── State derived from URL query params ───────────────────────────
  const view = (searchParams.get("view") as ViewMode) || "monthly";
  const selectedMonth =
    searchParams.get("selectedMonth") || latestDate || df.format(new Date(), "yyyy-MM-dd");
  const scheduleStartStop = searchParams.get("scheduleStartStop") || "";
  const operatingAreaParam = searchParams.get("operatingArea") || "";
  const intParam = searchParams.get("int") || "";
  const groupParam = searchParams.get("group") || "PLATFORM";
  const showEvents = searchParams.get("showEvents") !== "false";
  const atoDayOffset = parseInt(searchParams.get("atoDayOffset") || "360");
  const atoDateFormat = searchParams.get("ATODateFormat") || "Julian";

  // ── Working state ─────────────────────────────────────────────────
  const [missions, setMissions] = useState<ScheduleMission[]>([]);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [loading, setLoading] = useState(false);
  const [areaOptions, setAreaOptions] = useState<string[]>(OPERATING_AREAS);
  const [selectedAreas, setSelectedAreas] = useState<string[]>(
    operatingAreaParam ? operatingAreaParam.split(",").filter(Boolean) : OPERATING_AREAS
  );
  const hadAreaParam = !!operatingAreaParam;
  useEffect(() => {
    fetch("/api/operatingAreas")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const list = (Array.isArray(d) ? d : d?.resultsArray || [])
          .map((a: any) => a.short_name || a.name)
          .filter(Boolean);
        if (list.length === 0) return;
        setAreaOptions(list);
        // "All areas" was the default: keep it meaning all, including areas the fallback list lacked.
        if (!hadAreaParam) setSelectedAreas(list);
      })
      .catch(() => {});
  }, [hadAreaParam]);
  const [selectedInts, setSelectedInts] = useState<string[]>(
    intParam ? intParam.split(",").filter(Boolean) : INT_TYPES
  );
  const [groupBy, setGroupBy] = useState(groupParam);
  const [selectedMission, setSelectedMission] = useState<ScheduleMission | null>(null);
  const [sideOpen, setSideOpen] = useState(false);

  const openMissionDetail = (mission: ScheduleMission) => {
    setSelectedMission(mission);
    setSideOpen(true);
  };

  // ── Fetch platforms from API ─────────────────────────────────────
  useEffect(() => {
    fetch("/api/platforms")
      .then((res) => res.json())
      .then((data) => setPlatforms(data))
      .catch(() => {}); // silent fail — will use fallback colors
  }, []);

  // ── Build platform color map from fetched data ───────────────────
  const platformColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    platforms.forEach((p) => {
      const key = p.designator || p.name;
      if (p.color) map[key] = p.color;
    });
    return map;
  }, [platforms]);

  const getPlatformColor = (designator: string) => {
    return platformColorMap[designator] || "#c42ebe";
  };

  // 6-week calendar: compute the start/end weeks around selectedMonth
  const monthDate = useMemo(() => new Date(selectedMonth), [selectedMonth]);

  const calendarDays = useMemo(() => {
    if (view === "daily") {
      return [df.startOfDay(monthDate)];
    }
    if (view === "weekly") {
      const ws = df.startOfWeek(monthDate, { weekStartsOn: 0 });
      return df.eachDayOfInterval({ start: ws, end: df.addDays(ws, 6) });
    }
    const monthStart = df.startOfMonth(monthDate);
    const calStart = df.startOfWeek(monthStart, { weekStartsOn: 0 });
    const calEnd = new Date(calStart);
    calEnd.setDate(calStart.getDate() + 41);
    return df.eachDayOfInterval({ start: calStart, end: calEnd });
  }, [monthDate, view]);

  const weekStart = useMemo(() => df.startOfWeek(monthDate, { weekStartsOn: 0 }), [monthDate]);
  const weekEnd = useMemo(() => df.endOfWeek(monthDate, { weekStartsOn: 0 }), [monthDate]);

  const headerLabel = useMemo(() => {
    if (view === "daily") return df.format(monthDate, "EEEE, MMMM d, yyyy");
    if (view === "weekly") return `${df.format(weekStart, "MMM d")} - ${df.format(weekEnd, "MMM d, yyyy")}`;
    return df.format(monthDate, "MMMM yyyy");
  }, [view, monthDate, weekStart, weekEnd]);

  const calStart = df.startOfDay(calendarDays[0]);
  const calEnd = df.endOfDay(calendarDays[calendarDays.length - 1]);

  // ── Update URL params ────────────────────────────────────────────
  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // ── Fetch missions ───────────────────────────────────────────────
  useEffect(() => {
    const fetchMissions = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("start", calStart.toISOString());
        params.set("end", calEnd.toISOString());
        if (selectedAreas.length > 0) {
          params.set("operatingArea", selectedAreas.join(","));
        }
        if (selectedInts.length > 0) {
          params.set("int", selectedInts.join(","));
        }
        params.set("group", groupBy);

        const res = await fetch(`/api/schedule/missions?${params.toString()}`);
        if (res.ok) {
          const data = await res.json();
          setMissions(data);
        }
      } catch (err) {
        console.error("Failed to fetch schedule missions:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMissions();
  }, [calStart.toISOString(), calEnd.toISOString(), selectedAreas.join(","), selectedInts.join(","), groupBy]);

  // ── Sync filters to URL ─────────────────────────────────────────
  useEffect(() => {
    if (!datesReady) return;
    updateParams({
      view,
      selectedMonth: df.format(monthDate, "yyyy-MM-dd"),
      scheduleStartStop: `${df.format(calStart, "yyyy-MM-dd")},${df.format(calEnd, "yyyy-MM-dd")}`,
      operatingArea: selectedAreas.join(","),
      int: selectedInts.join(","),
      group: groupBy,
      showEvents: showEvents ? "true" : "false",
      ATODateFormat: atoDateFormat,
      atoDayOffset: atoDayOffset.toString(),
      scheduleSyncView: view,
      weekdayStart: "0",
      monthlyScheduleCardView: "Callsign,Launch Base,On/Off-Station",
    });
  }, [datesReady, view, monthDate, calStart, calEnd, selectedAreas, selectedInts, groupBy, showEvents, atoDateFormat, atoDayOffset, updateParams]);

  // ── Navigation ──────────────────────────────────────────────────
  const navigatePrev = () => {
    if (view === "monthly") {
      const newDate = df.subMonths(monthDate, 1);
      updateParams({ selectedMonth: df.format(newDate, "yyyy-MM-dd"), view });
    } else if (view === "weekly") {
      const newDate = df.subWeeks(monthDate, 1);
      updateParams({ selectedMonth: df.format(newDate, "yyyy-MM-dd"), view });
    } else if (view === "daily") {
      const newDate = df.subDays(monthDate, 1);
      updateParams({ selectedMonth: df.format(newDate, "yyyy-MM-dd"), view });
    }
  };

  const navigateNext = () => {
    if (view === "monthly") {
      const newDate = df.addMonths(monthDate, 1);
      updateParams({ selectedMonth: df.format(newDate, "yyyy-MM-dd"), view });
    } else if (view === "weekly") {
      const newDate = df.addWeeks(monthDate, 1);
      updateParams({ selectedMonth: df.format(newDate, "yyyy-MM-dd"), view });
    } else if (view === "daily") {
      const newDate = df.addDays(monthDate, 1);
      updateParams({ selectedMonth: df.format(newDate, "yyyy-MM-dd"), view });
    }
  };

  const goToToday = () => {
    const today = new Date();
    updateParams({ selectedMonth: df.format(today, "yyyy-MM-dd"), view });
  };
  const goToLatest = () => {
    if (latestDate) updateParams({ selectedMonth: latestDate, view });
  };

  // ── Mission grouping for cards ──────────────────────────────────
  const missionsByDay = useMemo(() => {
    const map: Record<string, ScheduleMission[]> = {};
    missions.forEach((m) => {
      // Real IDEX missions may lack a station window — bucket by the
      // occurrence date the API assigned, else on-station, else takeoff.
      const anchor = m.occurrenceDate || m.onStation || m.takeoff;
      if (anchor) {
        const dayKey = anchor.length === 10 ? anchor : df.format(df.parseISO(anchor), "yyyy-MM-dd");
        if (!map[dayKey]) map[dayKey] = [];
        map[dayKey].push(m);
      }
    });
    // Group = ordering inside a day cell: by platform, by asset (callsign), or by time.
    const timeOf = (m: ScheduleMission) => m.onStation || m.takeoff || "";
    Object.values(map).forEach((list) =>
      list.sort((a, b) => {
        if (groupBy === "PLATFORM") {
          const pa = a.platform?.designator || a.platform?.name || "";
          const pb = b.platform?.designator || b.platform?.name || "";
          if (pa !== pb) return pa.localeCompare(pb);
        } else if (groupBy === "ASSET") {
          if (a.callsign !== b.callsign) return a.callsign.localeCompare(b.callsign);
        }
        return timeOf(a).localeCompare(timeOf(b));
      })
    );
    return map;
  }, [missions, groupBy]);

  const getMissionsForDay = (day: Date): ScheduleMission[] => {
    const key = df.format(day, "yyyy-MM-dd");
    return missionsByDay[key] || [];
  };

  const renderDayCell = (day: Date, isCurrentMonth: boolean) => {
    const dayMissions = getMissionsForDay(day);
    const isToday = df.isSameDay(day, new Date());
    return (
      <Box key={day.toISOString()} sx={{ flex: 1, backgroundColor: isToday ? "rgba(196,46,190,0.08)" : "#12131b", p: 0.5, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", border: isToday ? "1px solid rgba(247,43,239,0.4)" : "none", "&:hover": { backgroundColor: "rgba(255,255,255,0.03)" } }}>
        <Typography sx={{ fontSize: "0.7rem", fontWeight: isToday ? 700 : 400, color: isCurrentMonth ? "#cacce5" : "#555", mb: 0.3, lineHeight: 1.1, flexShrink: 0 }}>{df.format(day, view === "daily" ? "EEEE, MMM d" : "d")}</Typography>
        <Box sx={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: "1px" }}>
          {dayMissions.slice(0, view === "daily" ? 50 : 6).map((mission) => {
            const platColor = getPlatformColor(mission.platform?.designator || "");
            const onTime = mission.onStation ? df.format(df.parseISO(mission.onStation), "HHmm") : "--";
            const offTime = mission.offStation ? df.format(df.parseISO(mission.offStation), "HHmm") : "--";
            return (
              <Box key={`${mission._id}-${mission.onStation || mission.takeoff}`} onClick={() => openMissionDetail(mission)} sx={{ backgroundColor: "rgba(26,28,38,0.85)", borderLeft: `3px solid ${platColor}`, borderRadius: "2px", px: 0.5, py: 0.3, fontSize: "0.6rem", lineHeight: 1.2, cursor: "pointer", "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" }, opacity: mission.canx ? 0.4 : mission.hold ? 0.6 : 1 }} title={`${mission.callsign} | ${mission.platform?.fullName || ""} | ${mission.launchBase} | ${onTime}Z-${offTime}Z | ${mission.operatingArea}`}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                  {mission.armed && (<Box component="span" sx={{ color: "#ff1744", fontSize: "0.5rem" }}>⚡</Box>)}
                  <Typography component="span" sx={{ fontWeight: 700, color: mission.hold ? "#ff9100" : mission.canx ? "#666" : "#fff", fontSize: "0.6rem" }}>{mission.callsign}</Typography>
                  {mission.locked && (<Box component="span" sx={{ color: "#f72bef", fontSize: "0.5rem", ml: "auto" }}>🔒</Box>)}
                </Box>
                {view === "monthly" ? (
                  <Typography sx={{ color: "#8a90a0", fontSize: "0.55rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {mission.launchBase} <Box component="span" sx={{ color: "#00b8ff" }}>{onTime}/{offTime}z</Box>
                  </Typography>
                ) : (
                  <>
                    <Typography sx={{ color: "#8a90a0", fontSize: "0.55rem" }}>{mission.launchBase}</Typography>
                    <Typography sx={{ color: "#00b8ff", fontSize: "0.55rem" }}>{onTime}/{offTime}z</Typography>
                  </>
                )}
              </Box>
            );
          })}
          {view !== "daily" && dayMissions.length > 6 && (<Typography sx={{ color: "#f72bef", fontSize: "0.55rem", textAlign: "center" }}>+{dayMissions.length - 6} more</Typography>)}
        </Box>
      </Box>
    );
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <Box sx={{ flex: 1, minHeight: 0, p: 0.5, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* ── Toolbar ─────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          px: 1.5,
          py: 0.75,
          mb: 0.5,
          backgroundColor: "#1a1c26",
          border: "1px solid #333",
          borderRadius: 1,
        }}
      >
        {/* Date navigation */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton size="small" onClick={navigatePrev} sx={{ color: "#cacce5" }}>
            <ChevronLeft />
          </IconButton>
          <Button
            size="small"
            onClick={goToToday}
            startIcon={<Today />}
            sx={{ color: "#cacce5", textTransform: "none", fontSize: "0.8rem" }}
          >
            Today
          </Button>
          {latestDate && (
            <Tooltip title={`Latest ATO day with missions: ${latestDate}`}>
              <Button size="small" onClick={goToLatest} sx={{ color: "#00b8ff", textTransform: "none", fontSize: "0.8rem", minWidth: 0 }}>
                Latest
              </Button>
            </Tooltip>
          )}
          <IconButton size="small" onClick={navigateNext} sx={{ color: "#cacce5" }}>
            <ChevronRight />
          </IconButton>
        </Box>

        {/* Month label */}
        <Typography
          variant="h6"
          sx={{
            color: "#f72bef",
            fontWeight: "bold",
            fontSize: "1.1rem",
            minWidth: 180,
            textAlign: "center",
          }}
        >
          {headerLabel}
        </Typography>

        {/* View switcher */}
        <ToggleButtonGroup
          size="small"
          value={view}
          exclusive
          onChange={(_, val) => {
            if (val) updateParams({ view: val, selectedMonth: df.format(monthDate, "yyyy-MM-dd") });
          }}
          sx={{
            marginLeft: "auto",
            "& .MuiToggleButton-root": {
              color: "#8a90a0",
              borderColor: "#444",
              "&.Mui-selected": {
                color: "#f72bef",
                backgroundColor: "rgba(247, 43, 239, 0.1)",
              },
            },
          }}
        >
          <ToggleButton value="monthly">
            <Tooltip title="Monthly">
              <CalendarViewMonth fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="weekly">
            <Tooltip title="Weekly">
              <ViewWeek fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="daily">
            <Tooltip title="Daily">
              <ViewDay fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Operating Area filter */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel sx={{ color: "#8a90a0", fontSize: "0.75rem" }}>Op Area</InputLabel>
          <Select
            multiple
            value={selectedAreas}
            onChange={(e) => setSelectedAreas(e.target.value as string[])}
            input={<OutlinedInput label="Op Area" />}
            renderValue={(selected) => (
              selected.length === areaOptions.length ? (
                <Typography sx={{ fontSize: "0.75rem", color: "#f72bef" }}>All areas ({selected.length})</Typography>
              ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.3 }}>
                {selected.map((v) => (
                  <Chip key={v} label={v} size="small" sx={{ height: 20, fontSize: "0.65rem", backgroundColor: "rgba(196,46,190,0.2)", color: "#f72bef" }} />
                ))}
              </Box>
              )
            )}
            MenuProps={{
              PaperProps: {
                sx: { backgroundColor: "#1a1c26", border: "1px solid #333", maxHeight: 300 },
              },
            }}
            sx={{
              color: "#cacce5",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f72bef" },
              fontSize: "0.8rem",
            }}
          >
            {areaOptions.map((area) => (
              <MenuItem key={area} value={area} dense>
                <Checkbox checked={selectedAreas.indexOf(area) > -1} size="small" sx={{ color: "#8a90a0" }} />
                <ListItemText primary={area} sx={{ color: "#cacce5", fontSize: "0.8rem" }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* INT type filter */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel sx={{ color: "#8a90a0", fontSize: "0.75rem" }}>INT</InputLabel>
          <Select
            multiple
            value={selectedInts}
            onChange={(e) => setSelectedInts(e.target.value as string[])}
            input={<OutlinedInput label="INT" />}
            renderValue={(selected) => (
              selected.length === INT_TYPES.length ? (
                <Typography sx={{ fontSize: "0.75rem", color: "#00b8ff" }}>All INT</Typography>
              ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.3 }}>
                {selected.map((v) => (
                  <Chip key={v} label={v} size="small" sx={{ height: 20, fontSize: "0.65rem", backgroundColor: "rgba(0,184,255,0.2)", color: "#00b8ff" }} />
                ))}
              </Box>
              )
            )}
            MenuProps={{
              PaperProps: {
                sx: { backgroundColor: "#1a1c26", border: "1px solid #333" },
              },
            }}
            sx={{
              color: "#cacce5",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f72bef" },
              fontSize: "0.8rem",
            }}
          >
            {INT_TYPES.map((int) => (
              <MenuItem key={int} value={int} dense>
                <Checkbox checked={selectedInts.indexOf(int) > -1} size="small" sx={{ color: "#8a90a0" }} />
                <ListItemText primary={int} sx={{ color: "#cacce5", fontSize: "0.8rem" }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Group by */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel sx={{ color: "#8a90a0", fontSize: "0.75rem" }}>Group</InputLabel>
          <Select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            input={<OutlinedInput label="Group" />}
            sx={{
              color: "#cacce5",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f72bef" },
              fontSize: "0.8rem",
            }}
          >
            {GROUP_OPTIONS.map((g) => (
              <MenuItem key={g} value={g} dense>
                {g}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Loading indicator */}
        {loading && (
          <Typography sx={{ color: "#8a90a0", fontSize: "0.75rem", ml: 1 }}>Loading...</Typography>
        )}
      </Paper>

            {/* Calendar Grid */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {view !== "daily" && (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "1px", backgroundColor: "#333", borderBottom: "1px solid #333" }}>
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <Box key={day} sx={{ py: 0.8, textAlign: "center", backgroundColor: "#1a1c26", color: "#8a90a0", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>{day}</Box>
            ))}
          </Box>
        )}

        {view === "monthly" && (
          <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gridTemplateRows: "repeat(6, 1fr)", gap: "1px", backgroundColor: "#333", overflow: "auto" }}>
            {calendarDays.map((day: Date) => renderDayCell(day, df.isSameMonth(day, monthDate)))}
          </Box>
        )}

        {view === "weekly" && (
          <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gridTemplateRows: "1fr", gap: "1px", backgroundColor: "#333", overflow: "auto" }}>
            {calendarDays.map((day: Date) => renderDayCell(day, true))}
          </Box>
        )}

        {view === "daily" && (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", backgroundColor: "#333", gap: "1px" }}>
            {renderDayCell(calendarDays[0], true)}
          </Box>
        )}
      </Box>
{/* ── Legend ──────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          mt: 1,
          px: 1,
          py: 0.5,
          backgroundColor: "#1a1c26",
          borderRadius: 1,
          border: "1px solid #333",
        }}
      >
        <Typography sx={{ color: "#8a90a0", fontSize: "0.65rem" }}>Platforms:</Typography>
        {platforms.filter((p) => missions.some((m) => (m.platform?.designator || m.platform?.name) === (p.designator || p.name))).map((p) => (
          <Box key={p._id} sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: p.color || "#c42ebe" }} />
            <Typography sx={{ color: "#cacce5", fontSize: "0.6rem" }}>{p.designator || p.name}</Typography>
          </Box>
        ))}
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
          <Chip label="⚡ Armed" size="small" sx={{ height: 18, fontSize: "0.55rem", backgroundColor: "rgba(255,23,68,0.15)", color: "#ff1744" }} />
          <Chip label="🔒 Locked" size="small" sx={{ height: 18, fontSize: "0.55rem", backgroundColor: "rgba(247,43,239,0.15)", color: "#f72bef" }} />
          <Chip label="Hold" size="small" sx={{ height: 18, fontSize: "0.55rem", backgroundColor: "rgba(255,145,0,0.15)", color: "#ff9100" }} />
        </Box>
      </Box>

      {/* ── Mission Detail Side Drawer ─────────────────────────── */}
      <Drawer
        anchor="right"
        open={sideOpen}
        onClose={() => setSideOpen(false)}
        PaperProps={{
          sx: {
            width: 380,
            backgroundColor: "#1a1c26",
            borderLeft: "1px solid #333",
            p: 2.5,
          },
        }}
      >
        {selectedMission && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <IconButton onClick={() => setSideOpen(false)} sx={{ color: "#cacce5" }}>
                <ChevronLeft />
              </IconButton>
              <Typography variant="h6" sx={{ color: "#f72bef", fontWeight: "bold", fontSize: "1rem" }}>
                {selectedMission.callsign}
              </Typography>
            </Box>
            <Divider sx={{ borderColor: "#333", mb: 2 }} />

            {/* Status chips */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
              {selectedMission.armed && <Chip label="Armed" size="small" sx={{ height: 20, fontSize: "0.6rem", backgroundColor: "rgba(255,23,68,0.15)", color: "#ff1744" }} />}
              {selectedMission.locked && <Chip label="Locked" size="small" sx={{ height: 20, fontSize: "0.6rem", backgroundColor: "rgba(247,43,239,0.15)", color: "#f72bef" }} />}
              {selectedMission.hold && <Chip label="Hold" size="small" sx={{ height: 20, fontSize: "0.6rem", backgroundColor: "rgba(255,145,0,0.15)", color: "#ff9100" }} />}
              {selectedMission.canx && <Chip label="Cancelled" size="small" sx={{ height: 20, fontSize: "0.6rem", backgroundColor: "rgba(158,158,158,0.15)", color: "#9e9e9e" }} />}
            </Box>

            {/* Platform */}
            {selectedMission.platform && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ color: "#8a90a0", fontSize: "0.65rem", textTransform: "uppercase", mb: 0.3 }}>Platform</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: getPlatformColor(selectedMission.platform.designator || "") }} />
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.9rem" }}>
                    {selectedMission.platform.fullName || `${selectedMission.platform.name} ${selectedMission.platform.designator}`}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Schedule */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ color: "#8a90a0", fontSize: "0.65rem", textTransform: "uppercase", mb: 0.3 }}>Schedule</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                <Box>
                  <Typography sx={{ color: "#555", fontSize: "0.6rem" }}>Launch Base</Typography>
                  <Typography sx={{ color: "#cacce5", fontSize: "0.75rem", fontWeight: 600 }}>{selectedMission.launchBase}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ color: "#555", fontSize: "0.6rem" }}>Op Area</Typography>
                  <Typography sx={{ color: "#cacce5", fontSize: "0.75rem", fontWeight: 600 }}>{selectedMission.operatingArea}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ color: "#555", fontSize: "0.6rem" }}>On-Station</Typography>
                  <Typography sx={{ color: "#00e676", fontSize: "0.75rem", fontWeight: 600 }}>
                    {selectedMission.onStation ? df.format(df.parseISO(selectedMission.onStation), "dd MMM HH:mm'Z'") : "--"}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ color: "#555", fontSize: "0.6rem" }}>Off-Station</Typography>
                  <Typography sx={{ color: "#ff9100", fontSize: "0.75rem", fontWeight: 600 }}>
                    {selectedMission.offStation ? df.format(df.parseISO(selectedMission.offStation), "dd MMM HH:mm'Z'") : "--"}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* INT */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ color: "#8a90a0", fontSize: "0.65rem", textTransform: "uppercase", mb: 0.3 }}>Intelligence</Typography>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {selectedMission.int_prime && <Chip label={selectedMission.int_prime} size="small" sx={{ height: 20, fontSize: "0.6rem", backgroundColor: "rgba(0,184,255,0.15)", color: "#00b8ff" }} />}
                {selectedMission.int_secondary && <Chip label={selectedMission.int_secondary} size="small" sx={{ height: 20, fontSize: "0.6rem", backgroundColor: "rgba(0,230,118,0.15)", color: "#00e676" }} />}
              </Box>
            </Box>

            {/* ATO */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ color: "#8a90a0", fontSize: "0.65rem", textTransform: "uppercase", mb: 0.3 }}>ATO</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                <Box>
                  <Typography sx={{ color: "#555", fontSize: "0.6rem" }}>ATO Day</Typography>
                  <Typography sx={{ color: "#cacce5", fontSize: "0.75rem" }}>{selectedMission.ato_day || "--"}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ color: "#555", fontSize: "0.6rem" }}>Mission #</Typography>
                  <Typography sx={{ color: "#cacce5", fontSize: "0.75rem" }}>{selectedMission.ato_mission_number || "--"}</Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ borderColor: "#333", mb: 2 }} />
            <Button
              fullWidth
              variant="outlined"
              size="small"
              onClick={() => {
                setSideOpen(false);
                const s = new URLSearchParams();
                const anchor = selectedMission.occurrenceDate || selectedMission.onStation || selectedMission.takeoff;
                if (anchor) s.set("atoDate", anchor.length === 10 ? anchor : df.format(df.parseISO(anchor), "yyyy-MM-dd"));
                if (selectedMission.operatingArea) s.set("operatingArea", selectedMission.operatingArea);
                s.set("mission", selectedMission._id);
                navigate(`/main?${s.toString()}`);
              }}
              sx={{
                color: "#f72bef",
                borderColor: "#f72bef",
                textTransform: "none",
                fontSize: "0.75rem",
                "&:hover": { backgroundColor: "rgba(247,43,239,0.1)", borderColor: "#f72bef" },
              }}
            >
              View Full Mission Details
            </Button>
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
